import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  BarChart3, 
  RefreshCw, 
  ChevronDown,
  ChevronUp,
  FileText,
  Volume2,
  Image as ImageIcon,
  AlertTriangle
} from 'lucide-react';
import api from '../utils/api';

interface QuestionResult {
  id: string;
  order: number;
  questionType: 'MCQ' | 'TYPE_IN';
  questionText: string;
  choiceA?: string;
  choiceB?: string;
  choiceC?: string;
  choiceD?: string;
  correctAnswer: string;
  explanation?: string;
  mediaFile?: string;
}

interface AnswerResult {
  id: string;
  studentAnswer: string;
  isCorrect: boolean;
  question: QuestionResult;
}

interface AttemptResult {
  id: string;
  startTime: string;
  endTime: string;
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  isOvertime: boolean;
  quiz: {
    id: string;
    title: string;
    subject: string;
    timeLimit?: number;
    creator: { name: string };
  };
  answers: AnswerResult[];
  student: {
    name: string;
  };
}

const QuizResultsPage: React.FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  
  const [result, setResult] = useState<AttemptResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (attemptId) {
      fetchResults();
    }
  }, [attemptId]);

  const fetchResults = async () => {
    try {
      setError('');
      const response = await api.get(`/quizzes/attempts/${attemptId}/results`);
      setResult(response.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestionExpanded = (questionId: string) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    if (!result) return;
    setExpandedQuestions(new Set(result.answers.map(a => a.question.id)));
  };

  const collapseAll = () => {
    setExpandedQuestions(new Set());
  };

  const formatDuration = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getSubjectColor = (subject: string) => {
    const colors = {
      VOCABULARY: 'bg-blue-100 text-blue-800',
      CLOZE: 'bg-green-100 text-green-800',
      COMPREHENSION: 'bg-purple-100 text-purple-800',
      MATHS: 'bg-orange-100 text-orange-800'
    };
    return colors[subject as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const handleRetakeQuiz = () => {
    navigate(`/quiz/${result?.quiz.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading results...</p>
        </div>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error || 'Results not found'}</p>
          <button onClick={() => navigate('/quizzes')} className="btn btn-primary">
            Back to Quizzes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/quizzes')}
            className="text-blue-600 hover:text-blue-700 mb-4"
          >
            ← Back to Quizzes
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900">{result.quiz.title} - Results</h1>
          <div className="flex items-center space-x-4 mt-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSubjectColor(result.quiz.subject)}`}>
              {result.quiz.subject}
            </span>
            <span className="text-gray-600">by {result.quiz.creator.name}</span>
          </div>
        </div>

        {/* Score Summary */}
        <div className={`rounded-lg p-8 mb-8 ${getScoreBgColor(result.score)}`}>
          <div className="text-center">
            <div className={`text-6xl font-bold mb-4 ${getScoreColor(result.score)}`}>
              {Math.round(result.score)}%
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="flex items-center justify-center mb-2">
                  <BarChart3 className="w-5 h-5 text-gray-600 mr-1" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{result.correctAnswers}</p>
                <p className="text-sm text-gray-600">Correct</p>
              </div>
              
              <div>
                <div className="flex items-center justify-center mb-2">
                  <XCircle className="w-5 h-5 text-gray-600 mr-1" />
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {result.totalQuestions - result.correctAnswers}
                </p>
                <p className="text-sm text-gray-600">Incorrect</p>
              </div>
              
              <div>
                <div className="flex items-center justify-center mb-2">
                  <Clock className="w-5 h-5 text-gray-600 mr-1" />
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {formatDuration(result.startTime, result.endTime)}
                </p>
                <p className="text-sm text-gray-600">Duration</p>
              </div>
              
              <div>
                <div className="flex items-center justify-center mb-2">
                  <FileText className="w-5 h-5 text-gray-600 mr-1" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{result.totalQuestions}</p>
                <p className="text-sm text-gray-600">Total Questions</p>
              </div>
            </div>
            
            {result.isOvertime && (
              <div className="mt-4 p-3 bg-orange-100 border border-orange-300 rounded-lg">
                <p className="text-orange-800 font-medium">
                  ⚠️ This attempt was submitted after the time limit
                </p>
              </div>
            )}
            
            <div className="mt-6">
              <button
                onClick={handleRetakeQuiz}
                className="btn btn-primary"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retake Quiz
              </button>
            </div>
          </div>
        </div>

        {/* Question Results */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Question Results</h2>
              
              <div className="flex space-x-2">
                <button
                  onClick={expandAll}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Expand All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={collapseAll}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Collapse All
                </button>
              </div>
            </div>
          </div>
          
          <div className="divide-y divide-gray-200">
            {result.answers
              .sort((a, b) => a.question.order - b.question.order)
              .map((answer) => {
                const isExpanded = expandedQuestions.has(answer.question.id);
                
                return (
                  <div key={answer.question.id} className="p-6">
                    {/* Question Header */}
                    <button
                      onClick={() => toggleQuestionExpanded(answer.question.id)}
                      className="w-full text-left"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4 flex-1">
                          <div className="flex-shrink-0">
                            {answer.isCorrect ? (
                              <CheckCircle className="w-6 h-6 text-green-600" />
                            ) : (
                              <XCircle className="w-6 h-6 text-red-600" />
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-sm font-medium text-gray-600">
                                Q{answer.question.order}
                              </span>
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                {answer.question.questionType}
                              </span>
                            </div>
                            
                            <p className="text-gray-900 font-medium">
                              {answer.question.questionText}
                            </p>
                            
                            <div className="mt-2 text-sm">
                              <span className={`font-medium ${answer.isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                                Your answer: {answer.studentAnswer}
                              </span>
                              {!answer.isCorrect && (
                                <span className="text-gray-600 ml-4">
                                  Correct: {answer.question.correctAnswer}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex-shrink-0 ml-4">
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </button>
                    
                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="mt-4 ml-10">
                        {/* Media */}
                        {answer.question.mediaFile && (
                          <div className="mb-4 p-4 border rounded-lg bg-gray-50">
                            <div className="flex items-center text-sm text-gray-600 mb-2">
                              {answer.question.mediaFile.includes('image') ? (
                                <ImageIcon className="w-4 h-4 mr-2" />
                              ) : answer.question.mediaFile.includes('audio') ? (
                                <Volume2 className="w-4 h-4 mr-2" />
                              ) : (
                                <FileText className="w-4 h-4 mr-2" />
                              )}
                              Media file
                            </div>
                            
                            {answer.question.mediaFile.includes('image') ? (
                              <img 
                                src={`/api/quizzes/media/${answer.question.mediaFile}`}
                                alt="Question media"
                                className="max-w-full h-auto rounded"
                              />
                            ) : answer.question.mediaFile.includes('audio') ? (
                              <audio controls className="w-full">
                                <source src={`/api/quizzes/media/${answer.question.mediaFile}`} />
                              </audio>
                            ) : (
                              <a 
                                href={`/api/quizzes/media/${answer.question.mediaFile}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-700"
                              >
                                View file
                              </a>
                            )}
                          </div>
                        )}
                        
                        {/* MCQ Options */}
                        {answer.question.questionType === 'MCQ' && (
                          <div className="mb-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">Answer choices:</p>
                            <div className="space-y-2">
                              {['A', 'B', 'C', 'D'].map(letter => {
                                const choice = answer.question[`choice${letter}` as keyof QuestionResult] as string;
                                if (!choice) return null;
                                
                                const isStudentChoice = answer.studentAnswer === choice;
                                const isCorrectChoice = answer.question.correctAnswer === choice;
                                
                                return (
                                  <div
                                    key={letter}
                                    className={`p-3 rounded border-2 ${
                                      isCorrectChoice
                                        ? 'border-green-500 bg-green-50'
                                        : isStudentChoice && !isCorrectChoice
                                        ? 'border-red-500 bg-red-50'
                                        : 'border-gray-200 bg-gray-50'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-900">
                                        <span className="font-medium">{letter}.</span> {choice}
                                      </span>
                                      
                                      <div className="flex items-center space-x-2">
                                        {isStudentChoice && (
                                          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                            Your choice
                                          </span>
                                        )}
                                        {isCorrectChoice && (
                                          <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                                            Correct
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Explanation */}
                        {answer.question.explanation && (
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm font-medium text-blue-900 mb-1">Explanation:</p>
                            <p className="text-blue-800">{answer.question.explanation}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizResultsPage;